import { useEffect, useRef } from 'react';
/** What to focus once the list has changed: a row by its place, the heading, or nothing to do (the row is still there). */
export function focusAfterRemoval(was, keys) {
    if (keys.includes(was.key))
        return null;
    if (!keys.length)
        return 'heading';
    return { index: Math.min(Math.max(0, was.index), keys.length - 1), control: was.control };
}
const FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), [tabindex="0"]';
/**
 * Keeps the keyboard in a list whose rows go away under it. `attr` is the attribute each row
 * carries its key in (`data-row` in the renderer's lists); `keys` are the rows now drawn, in order.
 * It acts only when the focus was lost — on <body> or on nothing — never taking it from where the
 * person has since moved it.
 */
export function useRowFocusKeeper(root, keys, attr = 'data-row') {
    const last = useRef(null);
    const current = useRef(keys);
    current.current = keys;
    useEffect(() => {
        const el = root.current;
        if (!el)
            return;
        const heard = (e) => {
            const target = e.target;
            const row = target?.closest?.(`[${attr}]`);
            if (!row || !el.contains(row))
                return;
            const key = row.getAttribute(attr) ?? '';
            last.current = { key, index: current.current.indexOf(key), control: target?.closest?.('[data-row-action]')?.getAttribute('data-row-action') ?? null };
        };
        el.addEventListener('focusin', heard);
        return () => el.removeEventListener('focusin', heard);
    }, [root, attr]);
    const joined = keys.join('\u0000');
    useEffect(() => {
        const was = last.current;
        const el = root.current;
        if (!was || !el || typeof document === 'undefined')
            return;
        const where = focusAfterRemoval(was, keys);
        if (where === null)
            return;
        last.current = null;
        const active = document.activeElement;
        if (active && active !== document.body && active.isConnected)
            return;
        if (where === 'heading') {
            [...document.querySelectorAll('#content h1, main h1')].find((h) => h.getClientRects().length)?.focus({ preventScroll: true });
            return;
        }
        const key = keys[where.index];
        const row = [...el.querySelectorAll(`[${attr}]`)].find((r) => r.getAttribute(attr) === key);
        if (!row)
            return;
        const same = where.control ? [...row.querySelectorAll('[data-row-action]')].find((b) => b.getAttribute('data-row-action') === where.control && !b.hasAttribute('disabled')) : undefined;
        (same ?? row.querySelector(FOCUSABLE))?.focus();
    }, [joined]); // eslint-disable-line react-hooks/exhaustive-deps
}
//# sourceMappingURL=row-focus.js.map